import React, {useCallback} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {trackPromise, usePromiseTracker} from "react-promise-tracker";
import {Subject} from "rxjs";
import {showToast} from "common";
import _ from "lodash";
import {useObservable} from "rxsupport";
import {ReportModal} from "../../form-components/ReportModal";
import {FatOutlineButton} from "common/component/FatOutlineButton";
import {LoadingSpinner} from "common";
import {ConsultWithCCMInput} from "../../domain";
import {ModalTextAreaInput} from "../../form-components/ModalTextAreaInput";

type Props = {
    visible$: Subject<boolean>;
    onFinished: (formValues: ConsultWithCCMInput | null) => Promise<void>;
};

export function ConsultWithCCMFormModal({visible$, onFinished}: Props) {
    const visible = useObservable(visible$, false);

    const methods = useForm<ConsultWithCCMInput>({
        defaultValues: {
            consultWithCCM: "",
            stepsTaken: ""
        }
    });

    const {promiseInProgress} = usePromiseTracker();

    const isCallingBackend = promiseInProgress;

    const submitWithTracking = useCallback((formValues: ConsultWithCCMInput | null) => trackPromise(onFinished(formValues)), [onFinished]);

    const onCancel = useCallback(async () => {
        showToast.warning(`Submission for Clinical Consult is cancelled`);
        methods.reset();
        visible$.next(false);
    }, [methods, visible$]);
    const submit = useCallback(async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        await methods.handleSubmit(async (data: ConsultWithCCMInput) => {
            await submitWithTracking(_.pick(data, [
                'consultWithCCM',
                'stepsTaken',
            ]));
            methods.reset();
            visible$.next(false);
        })(e);
    }, [methods, visible$, submitWithTracking]);


    return (
        <ReportModal visible={visible} title={'CCS Consult'} onClose={onCancel}>
            <FormProvider {...methods}>
                <form autoComplete="off" onSubmit={submit}>
                    {!isCallingBackend && (<>
                        <div className="px-6 py-4">
                            <div className="space-y-6">
                                <ModalTextAreaInput
                                    labelPosition="top"
                                    label={"Enter your clinical consultation summary here:"}
                                    disabled={isCallingBackend}
                                    field={"consultWithCCM"}
                                    placeholder="Enter your clinical consultation summary here..."
                                    required
                                    rowsNum={3}
                                    maxLength={4000}
                                    shouldStopInputOnMaxLength={false}
                                    showCharacterCount
                                    requiredMessage="Please provide a short summary regarding clinical consultation"
                                />
                                <ModalTextAreaInput
                                        labelPosition="top"
                                        label={"What steps have been taken so far?"}
                                        disabled={isCallingBackend}
                                        field={"stepsTaken"}
                                        placeholder="Describe the steps taken..."
                                        required
                                        rowsNum={3}
                                        maxLength={4000}
                                        shouldStopInputOnMaxLength={false}
                                        showCharacterCount
                                        requiredMessage="Please tell us what steps have been taken so far."
                                    />
                            </div>
                        </div>

                        <div className="w-full flex border-t dark:border-gray-600 p-4 justify-end">
                            <div className="flex w-1/4">
                                <FatOutlineButton color={'red'}
                                                  text={"Cancel"}
                                                  onClick={onCancel}/>
                                <span style={{width: 10}}/>
                                <FatOutlineButton type="submit" text={"Submit"} color={"green"}/>
                            </div>
                        </div>
                    </>)}

                    {isCallingBackend &&
                        <div className="p-24">
                            <LoadingSpinner text="Processing"/>
                        </div>
                    }
                </form>
            </FormProvider>
        </ReportModal>
    );
}
